import { FC, useEffect, useState } from "react";
import { useAuth } from "@/data/context/authContext";
import { REGISTRATION_STEPS } from "@/data";
import VisitRegistrationStep from "@/components/Registration/VisitRegistrationStep";
import QuestionnaireStep from "@/components/Registration/QuestionnaireStep";
import AccountCreationForm from "@/components/Registration/AccountCreationForm";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import ROUTES from "@/data/Routes";
import ServiceRegistration from "@/components/Registration/ServiceRegistration";
import Vo2MaxRegistrationStep from "@/components/Registration/Vo2MaxRegistrationStep";
import Loading from "@/components/Loading";
import Container from "@/components/Container";
import { UserJourneyPanel } from "@/components/Account/UserJourneyPanel";
import { ROLES } from "@/data/interfaces/RolesEnum";
import ordersService, {
  type OrderRegistrationPrefill,
} from "@/services/ordersService";
import { splitFullName } from "@/utils/splitFullName";
import { isUserInfoFilled } from "@/utils/isUserInfoFilled";
import userService from "@/services/userService";
import { NewUserData } from "@/data";

const SignUp: FC = () => {
  const {
    user,
    signUp,
    loadUser,
    markRegistrationFormSubmit,
    productToRegisterFor,
    goToNextProductRegistration,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [orderPrefillLoading, setOrderPrefillLoading] = useState(false);
  const [orderPrefill, setOrderPrefill] =
    useState<OrderRegistrationPrefill | null>(null);

  useEffect(() => {
    if (!orderId || user !== null) {
      setOrderPrefill(null);
      setOrderPrefillLoading(false);
      return;
    }

    let cancelled = false;
    setOrderPrefillLoading(true);

    ordersService
      .getOrderRegistrationPrefill(orderId)
      .then((data) => {
        if (!cancelled) setOrderPrefill(data);
      })
      .catch(() => {
        if (!cancelled) setOrderPrefill(null);
      })
      .finally(() => {
        if (!cancelled) setOrderPrefillLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, user]);

  if (user === null) {
    if (orderId && orderPrefillLoading) {
      return <Loading className="mt-20" />;
    }

    const externalPrefill = orderPrefill
      ? (() => {
          const { name, surname } = splitFullName(orderPrefill.full_name);
          return {
            name,
            surname,
            email: orderPrefill.email,
            phone: orderPrefill.phone,
          };
        })()
      : null;

    return (
      <AccountCreationForm
        onSubmit={signUp}
        externalPrefill={externalPrefill}
        orderCityHint={orderPrefill?.city}
      />
    );
  }

  if (user === undefined) return <Loading className="mt-20" />;

  if (!user.email_verified_at) {
    return (
      <section className="px-4 pb-14 md:px-6 md:pb-20">
        <Container className="pt-24 md:pt-32">
          <UserJourneyPanel />
        </Container>
      </section>
    );
  }

  if (user.role === ROLES.USER && !user.type_form_submitted) {
    return (
      <QuestionnaireStep
        onSubmit={() => setTimeout(markRegistrationFormSubmit, 4000)}
        productSteps={
          productToRegisterFor?.order_item.product.registration_step.steps
        }
      />
    );
  }

  if (user.role === ROLES.DOCTOR || user.role === ROLES.SUPER_ADMIN) {
    return (
      <Navigate
        to={user.isAdmin ? ROUTES.DASHBOARD : ROUTES.ACCOUNT}
        replace
      />
    );
  }

  if (!isUserInfoFilled(user)) {
    const handleUpdateUser = async (data: NewUserData) => {
      await userService.updateUser({
        surname: data.surname!,
        phone: data.phone!,
      });
      await loadUser();
      navigate(ROUTES.ACCOUNT, { replace: true });
    };

    return (
      <section className="px-4 pb-14 md:px-6 md:pb-20">
        <Container className="pt-24 md:pt-32">
          <AccountCreationForm
            onSubmit={handleUpdateUser}
            isUpdate
            user={user}
          />
        </Container>
      </section>
    );
  }

  if (productToRegisterFor == null) {
    return <Navigate to={ROUTES.ACCOUNT} replace />;
  }

  const productSteps =
    productToRegisterFor.order_item.product.registration_step.steps;
  const currentStep = productToRegisterFor.order_item.current_step;

  switch (currentStep) {
    case REGISTRATION_STEPS.CALENDLY_REG:
      return (
        <VisitRegistrationStep
          productSteps={productSteps}
          onEventScheduled={goToNextProductRegistration}
        />
      );
    case REGISTRATION_STEPS.MEDNET_REG:
      return (
        <ServiceRegistration
          submitCallback={goToNextProductRegistration}
          productSteps={productSteps}
        />
      );
    case REGISTRATION_STEPS.VO2MAX_CALENDLY_REG:
      return (
        <Vo2MaxRegistrationStep
          productSteps={productSteps}
          onEventScheduled={goToNextProductRegistration}
        />
      );
    case REGISTRATION_STEPS.DONE:
      return <Navigate to={ROUTES.ACCOUNT} replace />;
    default:
      console.error(
        `Couldn't resolve product registration step. Got: ${currentStep}`,
      );
      return "Kraunami duomenys....";
  }
};

export default SignUp;
